import { CamelCasedProperties, CamelCasedPropertiesDeep } from 'type-fest';
import { z } from 'zod/v4';
import { createThresholdOptionsSchema, stepOptionsSchema, unitOptionsSchema } from '../../utils';
import { colorPaletteKindSchema } from '../../utils/color';
import { createDefinitionSchema } from '../../utils/helpers';
import { legendSpecOptionsSchema } from '../../utils/legend';
import { tooltipModeSchema, tooltipSortSchema } from '../../utils/options';
import { newOverrideObject, visualOverrideSchema } from '../../utils/overrides';

export interface TimeSeriesChartYAxisOptions extends z.TypeOf<typeof timeSeriesChartYAxisOptionsSchema> {}

export const timeSeriesAxisScaleSchema = z.enum(['Linear', 'Log10', 'Log2']);
export type TimeSeriesAxisScale = z.infer<typeof timeSeriesAxisScaleSchema>;

export const isLogScale = (scale?: TimeSeriesAxisScale) => scale === 'Log10' || scale === 'Log2';

const timeSeriesChartYAxisOptionsSchema = z.object({
  show: z.boolean().optional(),
  label: z.string().optional(),
  unit: unitOptionsSchema.optional(),
  // N.B.: Some customer schemas have null stored for min and max, so we need to accept it as valid.
  // We use z.preprocess to convert null to undefined before validation.
  min: z.preprocess((val) => (val === null ? undefined : val), z.number().optional()),
  max: z.preprocess((val) => (val === null ? undefined : val), z.number().optional()),
  splitNumber: z.number().optional(),
  scale: timeSeriesAxisScaleSchema.optional(),
});

export interface TooltipSpecOptions extends z.TypeOf<typeof tooltipSpecOptionsSchema> {}
const tooltipSpecOptionsSchema = z.object({
  mode: tooltipModeSchema.optional(),
  enable_pinning: z.boolean().optional(),
  sort: tooltipSortSchema.optional(),
});

// None is equivalent to undefined since stack is optional
export type StackOptions = z.infer<typeof stackOptionsSchema>;
const stackOptionsSchema = z.enum(['None', 'All', 'Percent']);

export const isStackEnabled = (stack?: StackOptions) => stack === 'All' || stack === 'Percent';

export const isPercentStackEnabled = (stack?: StackOptions) => stack === 'Percent';

export type NullBehaviorOptions = z.infer<typeof nullBehaviorOptionsSchema>;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const nullBehaviorOptionsSchema = z.enum(['Connected', 'NullAsZero']);

export interface TimeSeriesChartVisualOptions extends z.TypeOf<typeof timeSeriesChartVisualOptionsSchema> {}

export type YAxisMode = z.infer<typeof yAxisMode>;
const yAxisMode = z.enum(['left', 'right']);

export interface TimeSeriesVisualOverride extends z.TypeOf<typeof timeseriesVisualOverrideSchema> {}
export const timeseriesVisualOverrideSchema = z
  .object({
    y_axis: yAxisMode.optional(),
  })
  .merge(visualOverrideSchema);

export interface TimeSeriesOverrides extends z.TypeOf<typeof timeseriesOverridesSchema> {}
export const timeseriesOverridesSchema = z.array(newOverrideObject(timeseriesVisualOverrideSchema));

const timeSeriesChartVisualOptionsSchema = z.object({
  display: z.enum(['line', 'bar']).optional(),
  line_width: z.number().optional(),
  area_opacity: z.number().optional(),
  show_points: z.enum(['Always', 'Never']).optional(),
  colorPalette: colorPaletteKindSchema.optional(),
  point_radius: z.number().describe('DEPRECATED').optional(), // [DEPRECATED] This property has no effect as of PR #48469
  stack: stackOptionsSchema.optional(),
  connect_nulls: z.boolean().describe('DEPRECATED: Please use null_behvavior instead.').optional(), // [DEPRECATED] Use null_behavior instead, cleanup when schema migration is ready
  null_behavior: z.enum(['Connected', 'NullAsZero']).optional(),
});

const timeSeriesChartStepOptionsSchema = stepOptionsSchema.extend({
  area_opacity: z.number().nonnegative().max(1).optional(),
  description: z.string().optional(),
});

export const timeSeriesChartThresholdOptionsSchema = createThresholdOptionsSchema(
  timeSeriesChartStepOptionsSchema
).extend({
  scale_axis: z.boolean().optional(),
});

export interface TimeSeriesChartStepOption extends z.TypeOf<typeof timeSeriesChartStepOptionsSchema> {}
export interface TimeSeriesChartThresholdOptions extends z.TypeOf<typeof timeSeriesChartThresholdOptionsSchema> {}

/**
 * Type for the spec-driven `DashboardPanel` variant of the time series chart.
 */
export interface TimeSeriesChartSpec extends z.TypeOf<typeof timeSeriesChartSpecSchema> {}

/**
 * Zod schema for the spec-driven `DashboardPanel` variant of the time series chart.
 */
export const timeSeriesChartSpecSchema = z.object({
  legend: legendSpecOptionsSchema.optional(),
  y_axis: timeSeriesChartYAxisOptionsSchema.optional(),
  y_axis_right: timeSeriesChartYAxisOptionsSchema.optional(),
  thresholds: timeSeriesChartThresholdOptionsSchema.optional(),
  visual: timeSeriesChartVisualOptionsSchema.optional(),
  tooltip: tooltipSpecOptionsSchema.optional(),
  overrides: timeseriesOverridesSchema.optional(),
});

/**
 * The schema for a TimeSeriesChart `DashboardPanel` definition.
 */
export interface TimeSeriesChartDefinition extends z.TypeOf<typeof timeseriesChartDefinitionSchema> {}
export const timeseriesChartDefinitionSchema = createDefinitionSchema('TimeSeriesChart', timeSeriesChartSpecSchema);

/**
 * Version of the Zod schema to be used for base types for the props-driven `Viz` variant of the time series chart.
 *
 * Currently, it is the same as the spec-driven variant, but it may need to change in the future to
 * do things like flatten a specific property or some other transform (examples can be found in pie chart).
 */
export const timeSeriesChartVizSchema = timeSeriesChartSpecSchema
  .omit({
    // Remove tooltip props and replace them with a flattened, slightly renamed version for `Viz` use case.
    tooltip: true,
    // Remove legend from the spec because we use a modified version in the `Viz` use case that is defined in
    // `viz-framework`.
    legend: true,
  })
  .extend({
    tooltipMode: tooltipSpecOptionsSchema.shape.mode,
    tooltipSort: tooltipSpecOptionsSchema.shape.sort,
    enableTooltipPinning: tooltipSpecOptionsSchema.shape.enable_pinning,
  });

type TimeSeriesChartVizSchemaType = z.TypeOf<typeof timeSeriesChartVizSchema>;

/**
 * Enum of time series properties that should only be camelCased shallowly when translated for the `Viz` use case.
 */
type TimeSeriesChartShallowCamelProperties = 'y_axis' | 'y_axis_right' | 'visual' | 'thresholds';

/**
 * Base type for the props-driven `Viz` variant of the time series chart generated from the Zod schema and
 * converting some snake_case properties to camelCase. This interface should be extended in `viz-framework`
 * to do the following:
 * - Add additional types for `Viz` only use cases.
 * - Extend properties to add more meaningful jsdoc. This is necessary because both `z.Typeof` and
 *   `CamelCasedPropertiesDeep` lose jsdoc.
 */
export interface TimeSeriesChartPropsBase
  extends CamelCasedPropertiesDeep<Omit<TimeSeriesChartVizSchemaType, TimeSeriesChartShallowCamelProperties>>,
    CamelCasedProperties<Pick<TimeSeriesChartVizSchemaType, TimeSeriesChartShallowCamelProperties>> {}
